import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { PerspectiveCamera, TrackballControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { Suspense, useCallback, useState } from "react";
import { ACESFilmicToneMapping, Group, Vector3 } from "three";
import { useBoolean } from "usehooks-ts";

import { Divider } from "../../components/ui-v2";
import { AnimationControls } from "./AnimationControls";
import { LightingControls } from "./LightingControls";
import { Lights } from "./Lights";
import { MaterialsControls } from "./MaterialsControls";
import { Model } from "./Model";
import { Skeleton } from "./Skeleton";
import { SkeletonControls } from "./SkeletonControls";

const DEFAULT_CAMERA_POSITION = new Vector3(1, 0, 0);

const Container = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const CanvasContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(4),
  left: theme.spacing(4),
  width: "280px",
  display: "flex",
  flexDirection: "column",
  borderRadius: "4px",
  border: `1px solid ${theme.palette.secondary[4]}`,
  backgroundColor: theme.palette.secondary[8],
}));

interface ModelViewProps {
  url: string;
  hideControls?: boolean;
}

export const ModelView = (props: ModelViewProps) => {
  const { url, hideControls = false } = props;

  const { value: rotateModel, toggle: toggleRotateModel } = useBoolean(true);
  const { value: showSkeleton, toggle: toggleShowSkeleton } = useBoolean(false);
  const [modelObject, setModelObject] = useState<Group | null>(null);
  const [hasSkeleton, setHasSkeleton] = useState(false);

  const handleControlsChange = () => {
    if (rotateModel) {
      toggleRotateModel();
    }
  };

  const handleModelLoaded = useCallback((model: Group) => {
    setModelObject(model);

    // Check if the model has a skeleton
    let foundBone = false;
    model.traverse((child) => {
      if (child.type === "Bone") {
        foundBone = true;
      }
    });
    setHasSkeleton(foundBone);
  }, []);

  return (
    <Container>
      <CanvasContainer>
        <Canvas
          gl={{ toneMapping: ACESFilmicToneMapping, toneMappingExposure: 1.0 }}
        >
          <PerspectiveCamera makeDefault position={DEFAULT_CAMERA_POSITION}>
            <Lights />
          </PerspectiveCamera>
          <Suspense fallback={null}>
            <Model
              url={url}
              rotate={rotateModel}
              showTextured={hideControls}
              onModelLoaded={handleModelLoaded}
            />
            {showSkeleton && hasSkeleton && (
              <Skeleton modelObject={modelObject} />
            )}
          </Suspense>
          <TrackballControls
            onChange={handleControlsChange}
            rotateSpeed={3.0}
            zoomSpeed={3.0}
          />
        </Canvas>
      </CanvasContainer>
      {!hideControls && (
        <ControlsContainer>
          <LightingControls />
          <Divider orientation="horizontal" />
          <MaterialsControls url={url} />
          <Divider orientation="horizontal" />
          <AnimationControls
            rotate={rotateModel}
            onToggleRotation={toggleRotateModel}
          />
          <Divider orientation="horizontal" />
          {hasSkeleton && (
            <SkeletonControls
              showSkeleton={showSkeleton}
              onToggleSkeleton={toggleShowSkeleton}
              disabled={!hasSkeleton}
            />
          )}
        </ControlsContainer>
      )}
    </Container>
  );
};
